/**
 * Toast - Success/Error notification
 *
 * Core component: No internal dependencies.
 * Includes useToast hook for state management.
 */

import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, X, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  /** Toast message */
  message: string;
  /** Toast type (determines color and icon) */
  type: ToastType;
  /** Callback when toast is dismissed */
  onClose: () => void;
  /** Auto-dismiss after ms (0 = no auto-dismiss) */
  duration?: number;
  /** Position on screen (default: top) */
  position?: 'top' | 'bottom';
}

const typeConfig: Record<ToastType, { icon: typeof CheckCircle; iconClass: string; textClass: string }> = {
  success: {
    icon: CheckCircle,
    iconClass: 'text-emerald-500',
    textClass: 'text-slate-700',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-rose-500',
    textClass: 'text-slate-700',
  },
  warning: {
    icon: AlertCircle,
    iconClass: 'text-amber-500',
    textClass: 'text-slate-700',
  },
  info: {
    icon: Info,
    iconClass: 'text-sky-500',
    textClass: 'text-slate-700',
  },
};

export function Toast({
  message,
  type,
  onClose,
  duration = 5000,
  position = 'top',
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const config = typeConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => setIsVisible(true));

    // Auto-dismiss
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
    return;
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match animation duration
  };

  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-50',
        'max-w-sm w-full mx-4',
        position === 'top' ? 'top-4' : 'bottom-4',
        'transition-all duration-200 ease-out',
        isVisible && !isLeaving
          ? 'opacity-100 translate-y-0'
          : position === 'top'
            ? 'opacity-0 -translate-y-2'
            : 'opacity-0 translate-y-2'
      )}
    >
      <div
        className="flex items-start gap-3 p-4 rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.08)]"
      >
        <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', config.iconClass)} />
        <p className={cn('flex-1 text-sm', config.textClass)}>{message}</p>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close notification"
          className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Hook for managing toast state
 */
export function useToast() {
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  return {
    toast,
    showToast,
    hideToast,
    success: (message: string) => showToast(message, 'success'),
    error: (message: string) => showToast(message, 'error'),
    warning: (message: string) => showToast(message, 'warning'),
    info: (message: string) => showToast(message, 'info'),
  };
}
